import Dexie from 'dexie';

export const db = new Dexie('sign-off');

const buildSearchText = (job) =>
  `${job.title || ''} ${job.client || ''} ${(job.tags || []).join(' ')}`
    .trim()
    .toLowerCase();

db.version(1).stores({
  jobs: '++id,createdAt,client,status,*tags',
  blobs: 'id',
  drafts: 'id',
  app: 'key'
});

db.version(2)
  .stores({
    jobs: '++id,createdAt,client,status,archivedAt,*tags',
    blobs: 'id',
    drafts: 'id',
    app: 'key',
    searchIndex: 'id,archivedAt'
  })
  .upgrade(async (transaction) => {
    const jobsTable = transaction.table('jobs');
    const searchIndexTable = transaction.table('searchIndex');
    const jobs = await jobsTable.toArray();

    if (jobs.length) {
      await jobsTable.bulkPut(
        jobs.map((job) => ({
          ...job,
          archivedAt: job.archivedAt ?? null
        }))
      );

      await searchIndexTable.bulkPut(
        jobs.map((job) => ({
          id: job.id,
          archivedAt: job.archivedAt ?? null,
          text: buildSearchText(job)
        }))
      );
    }
  });

db.version(3).stores({
  jobs: '++id,createdAt,client,status,archivedAt,*tags',
  blobs: 'id',
  drafts: 'id',
  app: 'key',
  searchIndex: 'id,archivedAt',
  settings: 'key',
  clients: '++id, name, updatedAt',
  postcodeCache: 'postcode, cachedAt'
});

export const SETTINGS_KEY = 'app';
export const LOGO_KEY = 'logo';

export const DEFAULT_SETTINGS = {
  business: {
    name: '',
    phone: '',
    email: '',
    address: ''
  },
  watermark: {
    mode: 'off', // 'off' | 'text' | 'logo'
    text: 'PROOF COPY',
    size: 36,
    colour: '#888888',
    opacity: 0.35,
    placement: 'centre-diagonal' // 'centre-diagonal' | 'bottom-right' | 'tiled'
  },
  signerTypes: [],
  logo: null
};

function mergeDeep(base, extra) {
  if (extra === null || extra === undefined) return base;
  if (typeof base !== 'object' || base === null) return extra;
  if (typeof extra !== 'object') return extra;

  const out = { ...base };

  for (const key of Object.keys(extra)) {
    const value = extra[key];

    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof base[key] === 'object' &&
      base[key] !== null
    ) {
      out[key] = mergeDeep(base[key], value);
    } else if (value !== undefined) {
      out[key] = value;
    }
  }

  return out;
}

export async function getSettings() {
  try {
    const stored = await db.settings.get(SETTINGS_KEY);
    const logoRecord = await db.settings.get(LOGO_KEY);

    return mergeDeep(DEFAULT_SETTINGS, {
      ...(stored?.value || {}),
      logo: logoRecord ? logoRecord.value : null
    });
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export async function saveSettings(partial) {
  const current = await getSettings();
  const next = mergeDeep(current, partial);

  next.logo = current.logo;

  await db.settings.put({
    key: SETTINGS_KEY,
    value: {
      business: next.business,
      watermark: next.watermark,
      signerTypes: next.signerTypes
    },
    updatedAt: Date.now()
  });

  return next;
}

export async function getLogo() {
  const record = await db.settings.get(LOGO_KEY);

  return record ? record.value : null;
}

export async function saveLogo(record) {
  await db.settings.put({
    key: LOGO_KEY,
    value: record,
    updatedAt: Date.now()
  });
}

export async function clearLogo() {
  await db.settings.delete(LOGO_KEY);
}

export async function saveClient(name) {
  const clean = String(name || '').trim();

  if (!clean) return;

  const existing = await db.clients.where('name').equals(clean).first();

  if (existing) {
    await db.clients.update(existing.id, { updatedAt: Date.now() });
    return existing.id;
  }

  return db.clients.add({ name: clean, updatedAt: Date.now() });
}

export function listClients() {
  return db.clients.orderBy('name').toArray();
}

export const POSTCODE_CACHE_TTL_DAYS = 30;

export async function cachePostcode(key, results) {
  await db.postcodeCache.put({
    postcode: key,
    cachedAt: Date.now(),
    results
  });
}

export async function getCachedPostcode(key) {
  const record = await db.postcodeCache.get(key);

  if (!record) return null;
  if (Date.now() - record.cachedAt > POSTCODE_CACHE_TTL_DAYS * 86400000) {
    await db.postcodeCache.delete(key).catch(() => {});
    return null;
  }

  return record;
}

export async function purgeOldPostcodeCache(days = POSTCODE_CACHE_TTL_DAYS) {
  const cutoff = Date.now() - days * 86400000;

  return db.postcodeCache.where('cachedAt').below(cutoff).delete();
}

export const DRAFT_ID = 'active';

export function isQuotaError(error) {
  return (
    error?.name === 'QuotaExceededError' ||
    String(error?.message || '')
      .toLowerCase()
      .includes('quota')
  );
}

export async function saveDraft(draft) {
  try {
    await db.drafts.put({
      ...draft,
      id: DRAFT_ID,
      updatedAt: Date.now()
    });
  } catch (error) {
    if (isQuotaError(error)) {
      throw new Error(
        'Device storage is full. Delete old records or export a backup, then try again.'
      );
    }

    throw error;
  }
}

export function loadDraft() {
  return db.drafts.get(DRAFT_ID);
}

export function clearDraft() {
  return db.drafts.delete(DRAFT_ID);
}

export async function saveApprovedJob(metadata, payload) {
  try {
    return await db.transaction(
      'rw',
      db.jobs,
      db.blobs,
      db.drafts,
      db.searchIndex,
      async () => {
        const id = await db.jobs.add({
          ...metadata,
          archivedAt: null
        });

        await db.blobs.put({ id, ...payload });
        await db.searchIndex.put({
          id,
          archivedAt: null,
          text: buildSearchText(metadata)
        });
        await db.drafts.delete(DRAFT_ID);

        return id;
      }
    );
  } catch (error) {
    if (isQuotaError(error)) {
      throw new Error(
        'There is not enough device storage to save this proof. Free space in Settings and try again.'
      );
    }

    throw error;
  }
}

export async function deleteJob(id) {
  await db.transaction(
    'rw',
    db.jobs,
    db.blobs,
    db.searchIndex,
    async () => {
      await db.jobs.delete(id);
      await db.blobs.delete(id);
      await db.searchIndex.delete(id);
    }
  );
}